'use client';

import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { useState } from 'react';
import PhoneField from '@/components/ui/PhoneField';
import { sendLead, lastLeadError } from '@/lib/sendLead';
import { downloadCataloguePdf } from '@/lib/pdf';
import { subscribeNewsletter } from '@/lib/subscribeNewsletter';
import { track } from '@/lib/analytics';
import { isValidEmail, isValidPhone, toE164 } from '@/lib/validation';
import { DEFAULT_COUNTRY } from '@/lib/countries';
import { CONTACT } from '@/lib/contact';
import type { Product } from '../productsData';

const MAX_MESSAGE = 500;

interface InquiryFormProps {
  selectedInquiryType?: string | null;
  /** Live catalogue  - drives the per-material catalogue PDFs and the product pickers. */
  products?: Product[];
  /** Admin-uploaded full catalogue PDF, offered alongside the generated ones. */
  catalogueUrl?: string | null;
  /** Product the visitor asked about (from a card / detail CTA) - seeds the picker. */
  prefillProduct?: Product | null;
}

type InquiryErrors = {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  consent?: string;
  scope?: string;
};

type CatalogueMaterial = 'areca' | 'bagasse';
const MATERIAL_LABEL: Record<CatalogueMaterial, string> = {
  areca: 'Areca Palm Leaf',
  bagasse: 'Sugarcane Bagasse',
};

type Scope = 'single' | 'multiple' | 'general';
const SCOPE_OPTIONS: { id: Scope; label: string; icon: string }[] = [
  { id: 'single', label: 'A specific product', icon: 'CubeIcon' },
  { id: 'multiple', label: 'Multiple products', icon: 'ClipboardDocumentListIcon' },
  { id: 'general', label: 'General enquiry', icon: 'ChatBubbleLeftRightIcon' },
];

const fieldClass =
  'w-full px-5 py-3.5 bg-background border border-border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50';

export default function InquiryForm({
  selectedInquiryType,
  products = [],
  catalogueUrl,
  prefillProduct = null,
}: InquiryFormProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    dialCode: DEFAULT_COUNTRY.dialCode,
    phone: '',
    quantity: '',
    deliveryLocation: '',
    message: '',
    consent: true,
    newsletter: true,
  });

  // Enquiry scope: a specific product, several products, or a general question.
  const [scope, setScope] = useState<Scope>(prefillProduct ? 'single' : 'general');
  const [singleProductId, setSingleProductId] = useState<string>(prefillProduct?.id ?? '');
  const [multiProductIds, setMultiProductIds] = useState<Set<string>>(new Set());

  const [errors, setErrors] = useState<InquiryErrors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [website, setWebsite] = useState(''); // honeypot
  const [downloadingMaterial, setDownloadingMaterial] = useState<CatalogueMaterial | null>(null);
  const [catalogueDownloaded, setCatalogueDownloaded] = useState(false);

  const isCatalogue = selectedInquiryType === 'Catalogue';

  const productGroups = (['bagasse', 'areca'] as const)
    .map((m) => ({
      material: m,
      label: MATERIAL_LABEL[m],
      items: products.filter((p) => p.material === m),
    }))
    .filter((g) => g.items.length > 0);

  const getTitle = () => {
    switch (selectedInquiryType) {
      case 'Sample':
        return 'Request Samples';
      case 'Quote':
        return 'Request a Quote';
      case 'Catalogue':
        return 'Download Catalogue';
      case 'Custom':
        return 'Custom Print Enquiry';
      default:
        return 'Product Enquiry';
    }
  };

  const getSubtitle = () => {
    switch (selectedInquiryType) {
      case 'Sample':
        return 'Get physical product samples delivered to your door, perfect for testing and evaluation only (available in select regions)';
      case 'Quote':
        return 'Share your product, volumes and timeline and we’ll send tailored pricing';
      case 'Catalogue':
        return 'Download our latest catalogue  - a separate PDF for each material line';
      case 'Custom':
        return 'Custom printing, logo branding & personalised solutions';
      default:
        return 'Tell us how we can help you';
    }
  };

  const showQtyLocation =
    selectedInquiryType === 'Sample' ||
    selectedInquiryType === 'Custom' ||
    selectedInquiryType === 'Quote';

  const leadType =
    selectedInquiryType === 'Sample'
      ? 'sample'
      : selectedInquiryType === 'Quote'
        ? 'quote'
        : 'contact';

  const validateField = (name: keyof typeof formData): string => {
    switch (name) {
      case 'name':
        return formData.name.trim() ? '' : 'Please enter your name';
      case 'email':
        return isValidEmail(formData.email) ? '' : 'Please enter a valid email address';
      case 'phone':
        return isValidPhone(formData.phone) ? '' : 'Please enter a valid phone number';
      case 'message':
        return formData.message.trim() ? '' : 'Please add a few details about your request';
      case 'consent':
        return formData.consent ? '' : 'Please agree to be contacted so we can respond';
      default:
        return '';
    }
  };

  const validateScope = (): string => {
    if (isCatalogue) return '';
    if (scope === 'single' && !singleProductId) return 'Please choose a product';
    if (scope === 'multiple' && multiProductIds.size === 0)
      return 'Please choose at least one product';
    return '';
  };

  const validate = (): boolean => {
    const next: InquiryErrors = {};
    (['name', 'email', 'phone', 'message', 'consent'] as const).forEach((f) => {
      const msg = validateField(f);
      if (msg) next[f] = msg;
    });
    const scopeMsg = validateScope();
    if (scopeMsg) next.scope = scopeMsg;
    setErrors(next);
    if (Object.keys(next).length > 0) {
      requestAnimationFrame(() => {
        document.querySelector<HTMLElement>('#inquiry [aria-invalid="true"]')?.focus();
      });
      return false;
    }
    return true;
  };

  const handleBlur = (name: keyof typeof formData) => {
    setErrors((prev) => ({ ...prev, [name]: validateField(name) || undefined }));
  };

  const toggleMultiProduct = (id: string) =>
    setMultiProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectScope = (s: Scope) => {
    setScope(s);
    setErrors((prev) => (prev.scope ? { ...prev, scope: undefined } : prev));
  };

  // The product(s) this enquiry is about, for the lead record.
  const scopeLabel =
    scope === 'single'
      ? 'Specific product'
      : scope === 'multiple'
        ? 'Multiple products'
        : 'General enquiry';
  const singleTitle = products.find((p) => p.id === singleProductId)?.title;
  const multiTitles = products
    .filter((p) => multiProductIds.has(p.id))
    .map((p) => p.title)
    .join(', ');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError('');

    const productContext =
      scope === 'single' ? singleTitle : scope === 'multiple' ? multiTitles : undefined;

    const result = await sendLead({
      type: leadType,
      honeypot: website,
      subject: `${getTitle()}  - ${productContext || formData.company || formData.name || 'Product inquiry'}`,
      fields: {
        'Enquiry type': selectedInquiryType || 'Product Enquiry',
        'Enquiry about': scopeLabel,
        Product: scope === 'single' ? singleTitle : undefined,
        Products: scope === 'multiple' ? multiTitles : undefined,
        Name: formData.name,
        Email: formData.email,
        Company: formData.company,
        Phone: toE164(formData.dialCode, formData.phone),
        'Estimated quantity': formData.quantity,
        'Delivery location': formData.deliveryLocation,
        Message: formData.message,
        Consent: formData.consent ? 'Yes' : 'No',
      },
    });

    if (result === 'invalid') {
      setSubmitError(lastLeadError || 'Please check the details you entered.');
      setIsSubmitting(false);
      return;
    }
    if (result === 'rate-limited') {
      setSubmitError('Too many submissions in a short time. Please try again in a minute.');
      setIsSubmitting(false);
      return;
    }
    if (result === 'error') {
      setSubmitError('Something went wrong sending your request. Please try again or email us.');
      setIsSubmitting(false);
      return;
    }

    if (formData.newsletter) {
      void subscribeNewsletter(formData.email.trim());
      track('newsletter_signup', { source: 'products_form' });
    }
    track('generate_lead', {
      form: 'products',
      inquiry_type: selectedInquiryType || 'Product Enquiry',
      scope,
    });
    setIsSubmitting(false);
    setIsSubmitted(true);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      dialCode: DEFAULT_COUNTRY.dialCode,
      phone: '',
      quantity: '',
      deliveryLocation: '',
      message: '',
      consent: false,
      newsletter: true,
    });
    setScope(prefillProduct ? 'single' : 'general');
    setSingleProductId(prefillProduct?.id ?? '');
    setMultiProductIds(new Set());
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) =>
      prev[name as keyof InquiryErrors] ? { ...prev, [name]: undefined } : prev
    );
  };

  // Generate a SEPARATE catalogue PDF per material (areca / bagasse)  - one
  // product per row with all its sizes and dimensions. Never merged.
  const handleCatalogueDownload = async (material: CatalogueMaterial) => {
    setDownloadingMaterial(material);
    setCatalogueDownloaded(false);

    const pdfProducts = products
      .filter((p) => p.material === material)
      .map((p) => ({
        title: p.title,
        subtitle: p.subtitle,
        image: p.images?.[0]?.src || p.image || '',
        specs: (p.specs ?? []).map((s) => ({ label: s.label, code: s.code, dims: s.dims })),
      }));

    try {
      await downloadCataloguePdf({
        material,
        materialLabel: MATERIAL_LABEL[material],
        products: pdfProducts,
      });
      setCatalogueDownloaded(true);
      track('catalogue_download', { source: 'generated', material });
    } catch (err) {
      // Never leave a button stuck on "Preparing…" if generation fails.
      console.error('Catalogue download failed', err);
    } finally {
      setDownloadingMaterial(null);
    }
  };

  // The admin-uploaded full catalogue (Supabase serves it as an attachment).
  const handleFullCatalogue = () => {
    if (!catalogueUrl) return;
    const sep = catalogueUrl.includes('?') ? '&' : '?';
    const a = document.createElement('a');
    a.href = `${catalogueUrl}${sep}download`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setCatalogueDownloaded(true);
    track('catalogue_download', { source: 'uploaded' });
  };

  const materialCount = (material: CatalogueMaterial) =>
    products.filter((p) => p.material === material).length;

  const messageOver = formData.message.length > MAX_MESSAGE;

  return (
    <section id="inquiry" className="py-12 lg:py-16 bg-surface/50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="inline-block px-4 py-2 bg-accent/10 border border-accent/20 rounded-full font-headline text-xl lg:text-2xl font-bold text-accent mb-4">
            {getTitle() || 'Enquiry'}
          </h2>

          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto">
            {getSubtitle()}
          </p>
        </div>

        {isSubmitted ? (
          <div
            role="status"
            aria-live="polite"
            className="bg-success/10 border border-success/30 rounded-2xl p-12 text-center"
          >
            <div className="w-20 h-20 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={48} className="text-success" />
            </div>
            <h3 className="font-headline text-2xl font-bold text-foreground mb-3">Thank You!</h3>
            <p className="font-body text-lg text-muted-foreground">
              We have received your request. {CONTACT.responsePromise}.
            </p>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            noValidate
            className="bg-surface border border-border rounded-2xl p-6 lg:p-8"
          >
            {/* Honeypot */}
            <div aria-hidden="true" className="absolute w-px h-px overflow-hidden -left-[9999px]">
              <label htmlFor="inq-website">Website</label>
              <input
                id="inq-website"
                name="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            {/* Enquiry scope  - which product(s) is this about? */}
            {!isCatalogue && (
              <fieldset className="mb-6">
                <legend className="mb-2 block font-cta text-sm font-medium text-foreground">
                  What&apos;s your enquiry about? <span className="text-accent">*</span>
                </legend>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {SCOPE_OPTIONS.map((opt) => {
                    const active = scope === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        aria-pressed={active}
                        onClick={() => selectScope(opt.id)}
                        className={`flex items-center gap-2 rounded-xl border px-4 py-3 font-cta text-sm font-medium transition duration-200 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 ${
                          active
                            ? 'border-primary bg-primary/10 text-foreground'
                            : 'border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground'
                        }`}
                      >
                        <Icon
                          name={opt.icon}
                          size={18}
                          className={active ? 'text-primary' : ''}
                          aria-hidden="true"
                        />
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                {/* Single product select */}
                {scope === 'single' && products.length > 0 && (
                  <div className="mt-3">
                    <label htmlFor="inq-product" className="sr-only">
                      Select a product
                    </label>
                    <select
                      id="inq-product"
                      value={singleProductId}
                      onChange={(e) => {
                        setSingleProductId(e.target.value);
                        setErrors((prev) => (prev.scope ? { ...prev, scope: undefined } : prev));
                      }}
                      aria-invalid={!!errors.scope}
                      className={fieldClass}
                    >
                      <option value="">Select a product…</option>
                      {productGroups.map((g) => (
                        <optgroup key={g.material} label={g.label}>
                          {g.items.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.title}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </div>
                )}

                {/* Multiple products checklist */}
                {scope === 'multiple' && products.length > 0 && (
                  <div className="mt-3 rounded-xl border border-border bg-background p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="font-cta text-xs text-muted-foreground">
                        Choose all that apply
                      </span>
                      <span className="font-cta text-xs font-medium text-primary">
                        {multiProductIds.size} selected
                      </span>
                    </div>
                    <div className="max-h-56 space-y-3 overflow-y-auto pr-1">
                      {productGroups.map((g) => (
                        <div key={g.material}>
                          <p className="mb-1.5 font-cta text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            {g.label}
                          </p>
                          <div className="space-y-0.5">
                            {g.items.map((p) => (
                              <label
                                key={p.id}
                                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 transition hover:bg-muted"
                              >
                                <input
                                  type="checkbox"
                                  checked={multiProductIds.has(p.id)}
                                  onChange={() => {
                                    toggleMultiProduct(p.id);
                                    setErrors((prev) =>
                                      prev.scope ? { ...prev, scope: undefined } : prev
                                    );
                                  }}
                                  className="h-4 w-4 rounded border-border bg-background text-primary focus:ring-primary"
                                />
                                <span className="font-body text-sm text-foreground">{p.title}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {errors.scope && (
                  <p role="alert" className="mt-2 text-sm text-error">
                    {errors.scope}
                  </p>
                )}
              </fieldset>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Fields */}
              <div>
                <label
                  htmlFor="inq-name"
                  className="block font-cta text-sm font-medium text-foreground mb-2"
                >
                  Full Name <span className="text-accent">*</span>
                </label>
                <input
                  id="inq-name"
                  type="text"
                  name="name"
                  required
                  autoComplete="name"
                  value={formData.name}
                  onChange={handleChange}
                  onBlur={() => handleBlur('name')}
                  aria-required="true"
                  aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? 'inq-name-error' : undefined}
                  className={fieldClass}
                  placeholder="John Doe"
                />
                {errors.name && (
                  <p id="inq-name-error" role="alert" className="text-error text-sm mt-1">
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="inq-email"
                  className="block font-cta text-sm font-medium text-foreground mb-2"
                >
                  Email Address <span className="text-accent">*</span>
                </label>
                <input
                  id="inq-email"
                  type="email"
                  name="email"
                  required
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={() => handleBlur('email')}
                  aria-required="true"
                  aria-invalid={!!errors.email}
                  aria-describedby={errors.email ? 'inq-email-error' : undefined}
                  className={fieldClass}
                  placeholder="john@company.com"
                />
                {errors.email && (
                  <p id="inq-email-error" role="alert" className="text-error text-sm mt-1">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="inq-company"
                  className="block font-cta text-sm font-medium text-foreground mb-2"
                >
                  Company Name
                </label>
                <input
                  id="inq-company"
                  type="text"
                  name="company"
                  autoComplete="organization"
                  value={formData.company}
                  onChange={handleChange}
                  className={fieldClass}
                  placeholder="Your Company"
                />
              </div>

              <div>
                <label
                  htmlFor="inq-phone"
                  className="block font-cta text-sm font-medium text-foreground mb-2"
                >
                  Phone Number <span className="text-accent">*</span>
                </label>
                <PhoneField
                  id="inq-phone"
                  fieldClassName="bg-background rounded-xl"
                  dialCode={formData.dialCode}
                  onDialCodeChange={(dialCode) => setFormData((prev) => ({ ...prev, dialCode }))}
                  value={formData.phone}
                  onChange={(phone) => {
                    setFormData((prev) => ({ ...prev, phone }));
                    setErrors((prev) => (prev.phone ? { ...prev, phone: undefined } : prev));
                  }}
                  onBlur={() => handleBlur('phone')}
                  error={errors.phone}
                  errorId="inq-phone-error"
                  required
                />
                {errors.phone && (
                  <p id="inq-phone-error" role="alert" className="text-error text-sm mt-1">
                    {errors.phone}
                  </p>
                )}
              </div>
            </div>

            {/* Catalogue Specific Section  - separate PDF per material line */}
            {isCatalogue && (
              <div
                className="mt-8 p-6 bg-background/70 border border-border rounded-xl"
                role="group"
                aria-labelledby="catalogue-cat-label"
              >
                <span
                  id="catalogue-cat-label"
                  className="block font-cta text-sm font-medium text-foreground"
                >
                  Download our product catalogue
                </span>
                <p className="mt-1 text-sm text-muted-foreground">
                  Each material is a separate PDF - one product per row with every size and its
                  dimensions.
                </p>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {(['areca', 'bagasse'] as const).map((m) => {
                    const count = materialCount(m);
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => handleCatalogueDownload(m)}
                        disabled={downloadingMaterial !== null || count === 0}
                        className="flex items-center justify-between gap-2 py-3.5 px-5 bg-accent hover:bg-accent/90 text-accent-foreground font-cta font-semibold rounded-xl transition active:scale-[0.98] disabled:opacity-60 disabled:active:scale-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
                      >
                        <span className="text-left leading-tight">
                          {downloadingMaterial === m ? 'Preparing…' : MATERIAL_LABEL[m]}
                          <span className="block text-xs font-normal opacity-80">
                            {count} product{count === 1 ? '' : 's'}
                          </span>
                        </span>
                        <Icon name="ArrowDownTrayIcon" size={20} aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>

                {catalogueUrl && (
                  <button
                    type="button"
                    onClick={handleFullCatalogue}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 py-3 border border-border rounded-xl font-cta text-sm font-medium text-foreground transition hover:bg-muted active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    Or download the full uploaded catalogue (PDF)
                    <Icon name="ArrowDownTrayIcon" size={18} aria-hidden="true" />
                  </button>
                )}

                <p
                  className="mt-3 text-sm text-muted-foreground text-center"
                  role="status"
                  aria-live="polite"
                >
                  {catalogueDownloaded
                    ? 'Catalogue downloaded as a PDF.'
                    : 'Generates instantly as a PDF.'}
                </p>
              </div>
            )}

            {/* Quantity & Location - Shown for Sample, Custom & Quote */}
            {showQtyLocation && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label
                    htmlFor="inq-quantity"
                    className="block font-cta text-sm font-medium text-foreground mb-2"
                  >
                    Estimated Quantity
                  </label>
                  <input
                    id="inq-quantity"
                    type="text"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="e.g., 5,000 units"
                  />
                </div>
                <div>
                  <label
                    htmlFor="inq-location"
                    className="block font-cta text-sm font-medium text-foreground mb-2"
                  >
                    Delivery Location
                  </label>
                  <input
                    id="inq-location"
                    type="text"
                    name="deliveryLocation"
                    value={formData.deliveryLocation}
                    onChange={handleChange}
                    className={fieldClass}
                    placeholder="City, Country"
                  />
                </div>
              </div>
            )}

            {/* Message */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-2">
                <label
                  htmlFor="inq-message"
                  className="block font-cta text-sm font-medium text-foreground"
                >
                  Additional Details <span className="text-accent">*</span>
                </label>
                <span
                  aria-live="polite"
                  className={`font-body text-xs ${messageOver ? 'text-error' : 'text-muted-foreground'}`}
                >
                  {formData.message.length}/{MAX_MESSAGE}
                </span>
              </div>
              <textarea
                id="inq-message"
                name="message"
                required
                rows={3}
                maxLength={MAX_MESSAGE}
                value={formData.message}
                onChange={handleChange}
                onBlur={() => handleBlur('message')}
                aria-required="true"
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? 'inq-message-error' : undefined}
                className={`${fieldClass} resize-y`}
                placeholder={
                  selectedInquiryType === 'Custom'
                    ? 'Tell us about your logo, design, colors, quantity, and timeline...'
                    : 'Any specific requirements or notes...'
                }
              />
              {errors.message && (
                <p id="inq-message-error" role="alert" className="text-error text-sm mt-1">
                  {errors.message}
                </p>
              )}
            </div>

            {/* Consent */}
            {!isCatalogue && (
              <div className="mt-6 flex items-start gap-3">
                <input
                  id="inq-consent"
                  type="checkbox"
                  name="consent"
                  checked={formData.consent}
                  onChange={(e) => {
                    const consent = e.target.checked;
                    setFormData((prev) => ({ ...prev, consent }));
                    if (consent) setErrors((prev) => ({ ...prev, consent: undefined }));
                  }}
                  aria-required="true"
                  aria-invalid={!!errors.consent}
                  aria-describedby={errors.consent ? 'inq-consent-error' : undefined}
                  className="mt-1 w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                />
                <label htmlFor="inq-consent" className="font-body text-sm text-muted-foreground">
                  I agree to be contacted regarding my inquiry, in line with the{' '}
                  <Link href="/legal/privacy" className="text-info underline hover:text-info/80">
                    Privacy Policy
                  </Link>
                  . <span className="text-accent">*</span>
                </label>
              </div>
            )}
            {errors.consent && (
              <p id="inq-consent-error" role="alert" className="text-error text-sm mt-1">
                {errors.consent}
              </p>
            )}

            {!isCatalogue && (
              <div className="mt-4 flex items-start gap-3">
                <input
                  id="inq-newsletter"
                  type="checkbox"
                  name="newsletter"
                  checked={formData.newsletter}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, newsletter: e.target.checked }))
                  }
                  className="mt-1 w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                />
                <label htmlFor="inq-newsletter" className="font-body text-sm text-muted-foreground">
                  Subscribe for sustainability tips and product updates (monthly). You can
                  unsubscribe any time.
                </label>
              </div>
            )}

            {submitError && (
              <p role="alert" className="text-error text-sm mt-4">
                {submitError}
              </p>
            )}

            {/* Submit Button */}
            {!isCatalogue && (
              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-4 w-full py-4 bg-accent hover:bg-accent/90 text-accent-foreground font-cta font-semibold text-base rounded-xl transition active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 flex items-center justify-center gap-2 disabled:opacity-60 disabled:active:scale-100"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
                {!isSubmitting && <Icon name="PaperAirplaneIcon" size={20} />}
              </button>
            )}

            {/* <p className="font-body text-center text-xs text-muted-foreground mt-3">
              {CONTACT.responsePromise}.
            </p> */}
          </form>
        )}
      </div>
    </section>
  );
}
